import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { useQueryClient } from "@tanstack/react-query";
import { useState, useMemo, useEffect, useRef, ChangeEvent } from "react";
import { PreTransaction } from "@/app/services/authorizationService/getAuthorization";
import { AuthorizationStatusEnum } from "@/app/services/authorizationService/getAuthorization";
import { AuthorizationColumns } from "./components/AuthorizationColumns";
import { useAuthorizations, AuthorizationFilters } from "@/app/hooks/useAuthorizations";
import { PartnerType } from "@/app/utils/partners.enum";
import { useNavigate } from "react-router-dom";
import { useDebounce } from "@/app/hooks/useDebounce";
import { useProductsCatalog } from "@/app/hooks/useProductsCatalog";

export const useAuthorizationTableController = () => {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(500, search);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [filterStatus, setFilterStatus] = useState<AuthorizationStatusEnum[]>([]);
  const [filterType, setFilterType] = useState<PartnerType[]>([]);

  const filters: AuthorizationFilters = useMemo(() => ({
    search: debouncedSearch || undefined,
    status: filterStatus.length > 0 ? filterStatus : undefined,
    type: filterType.length > 0 ? filterType : undefined,
  }), [debouncedSearch, filterStatus, filterType]);

  const query = useAuthorizations(filters);
  const { catalog } = useProductsCatalog();
  const { columns } = AuthorizationColumns(catalog);

  const [selectedItem, setSelectedItem] = useState<PreTransaction | null>(null);
  const [simulatePanOpen, setSimulatePanOpen] = useState(false);

  const [sheetOpen, setSheetOpen] = useState(false);

  const handleSearchInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries({
      queryKey: ["authorizations"],
    });
  };

  const handleApplyFilter = (status: AuthorizationStatusEnum[], type: PartnerType[]) => {
    setFilterStatus(status);
    setFilterType(type);
  };

  const handleClearFilter = () => {
    setFilterStatus([]);
    setFilterType([]);
  };

  const hasActiveFilter = filterStatus.length > 0 || filterType.length > 0;

  const observerRef = useRef<HTMLDivElement>(null);

  const data = useMemo(() => {
    return query.data?.pages.flatMap((p) => p.preTransactions) ?? [];
  }, [query.data]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const handleRowClick = (item: PreTransaction) => {
    setSelectedItem(item);
    setSheetOpen(true);
  };

  const simulationMap: Record<string, () => void> = {
    [PartnerType.C6]: () => {
      if (!selectedItem?.customerId?.cpf) return;

      navigate(`/transacoes/${selectedItem.customerId.cpf}/c6`);
    },
    [PartnerType.PAN]: () => {
      if (!selectedItem?.customerId?.cpf) return;

      setSimulatePanOpen(true);
    },
    [PartnerType.CREFAZ_CLT]: () =>
      navigate(`/transacoes/crefaz-clt/${selectedItem?._id}`),
  };

  const handleSimulate = () => {
    if (!selectedItem) return;

    const simulate = simulationMap[selectedItem.type ?? PartnerType.C6];

    if (!simulate) return;

    simulate();
  };

  useEffect(() => {
    if (!observerRef.current || !query.hasNextPage || query.isFetchingNextPage)
      return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;

        if (entry.isIntersecting) {
          query.fetchNextPage();
        }
      },
      { rootMargin: "200px" },
    );

    observer.observe(observerRef.current);

    return () => observer.disconnect();
  }, [query.hasNextPage, query.isFetchingNextPage]);

  return {
    table,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    selectedItem,
    sheetOpen,
    setSheetOpen,
    handleRowClick,
    observerRef,
    isFetchingNextPage: query.isFetchingNextPage,
    shouldShowInfiniteScroll: query.hasNextPage,
    handleSimulate,
    simulatePanOpen,
    setSimulatePanOpen,
    search,
    handleSearchInputChange,
    handleRefresh,
    filterStatus,
    filterType,
    handleApplyFilter,
    handleClearFilter,
    hasActiveFilter,
    data,
    fetchNextPage: query.fetchNextPage,
  };
};
